import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useLang } from '@/contexts/LangContext';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

export default function PreferencesPage() {
  const { t, lang } = useLang();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testId = searchParams.get('test_id');

  const [languages, setLanguages] = useState([]);
  const [courseTypes, setCourseTypes] = useState([]);
  const [form, setForm] = useState({
    preferred_contact: '',
    course_format: '',
    heard_from: '',
    language_id: '',
    course_type_id: '',
    notes: '',
    placement_test_id: testId || '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      const redirectTarget = testId ? `/preferences?test_id=${encodeURIComponent(testId)}` : '/preferences';
      navigate(`/login?redirect=${encodeURIComponent(redirectTarget)}`);
    }
  }, [loading, user, navigate, testId]);

  useEffect(() => {
    api.get('/languages')
      .then((response) => setLanguages(Array.isArray(response.data) ? response.data : []))
      .catch(() => setLanguages([]));
  }, [lang]);

  useEffect(() => {
    if (!form.language_id) {
      setCourseTypes([]);
      setForm((current) => ({ ...current, course_type_id: '' }));
      return;
    }

    api.get('/course-types', { params: { language_id: form.language_id } })
      .then((response) => setCourseTypes(Array.isArray(response.data) ? response.data : []))
      .catch(() => setCourseTypes([]));
  }, [form.language_id, lang]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/preferences', form);
      toast.success(t('common.submit'));
      navigate('/');
    } catch {
      toast.error(t('alerts.submit_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="preferences-page" className="py-10 sm:py-14">
      <div className="mx-auto max-w-xl px-4 sm:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-8 text-center">
            <CheckCircle2 className="mx-auto mb-4 h-12 w-12 text-primary" />
            <h1 className="text-2xl font-semibold">{t('pref.title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t('pref.subtitle')}</p>
          </div>

          <Card className="rounded-2xl border shadow-sm">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="mb-2 block text-sm font-medium">{t('pref.contact_method')}</Label>
                  <RadioGroup value={form.preferred_contact} onValueChange={(value) => setForm((current) => ({ ...current, preferred_contact: value }))} className="flex flex-wrap gap-3">
                    {[['whatsapp', 'WhatsApp'], ['phone', t('pref.phone_call')], ['email', t('auth.email')]].map(([value, label]) => (
                      <div key={value} className="flex items-center space-x-2">
                        <RadioGroupItem value={value} id={`contact-${value}`} />
                        <Label htmlFor={`contact-${value}`} className="cursor-pointer text-sm">{label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-medium">{t('pref.course_format')}</Label>
                  <RadioGroup value={form.course_format} onValueChange={(value) => setForm((current) => ({ ...current, course_format: value }))} className="flex flex-wrap gap-3">
                    {[['private', t('pref.private')], ['group', t('pref.group')], ['mini_group', t('pref.mini_group')]].map(([value, label]) => (
                      <div key={value} className="flex items-center space-x-2">
                        <RadioGroupItem value={value} id={`format-${value}`} />
                        <Label htmlFor={`format-${value}`} className="cursor-pointer text-sm">{label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-medium">{t('pref.language')}</Label>
                  <Select value={form.language_id} onValueChange={(value) => setForm((current) => ({ ...current, language_id: value, course_type_id: '' }))}>
                    <SelectTrigger className="rounded-lg"><SelectValue placeholder={t('pref.select_language')} /></SelectTrigger>
                    <SelectContent>
                      {languages.map((language) => <SelectItem key={language.id} value={String(language.id)}>{language.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {courseTypes.length > 0 && (
                  <div>
                    <Label className="mb-2 block text-sm font-medium">{t('pref.course_type')}</Label>
                    <Select value={form.course_type_id} onValueChange={(value) => setForm((current) => ({ ...current, course_type_id: value }))}>
                      <SelectTrigger className="rounded-lg"><SelectValue placeholder={t('pref.select_type')} /></SelectTrigger>
                      <SelectContent>
                        {courseTypes.map((courseType) => <SelectItem key={courseType.id} value={String(courseType.id)}>{courseType.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="mb-2 block text-sm font-medium">{t('pref.heard_from')}</Label>
                  <Select value={form.heard_from} onValueChange={(value) => setForm((current) => ({ ...current, heard_from: value }))}>
                    <SelectTrigger className="rounded-lg"><SelectValue placeholder={t('pref.select_source')} /></SelectTrigger>
                    <SelectContent>
                      {[['website', t('pref.src_website')], ['email', t('pref.src_email')], ['google', t('pref.src_google')], ['facebook', 'Facebook'], ['instagram', 'Instagram'], ['linkedin', 'LinkedIn']].map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="mb-2 block text-sm font-medium">{t('pref.notes')}</Label>
                  <Textarea className="rounded-lg" placeholder={t('pref.notes_placeholder')} value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} />
                </div>

                <Button type="submit" className="h-11 w-full rounded-xl" disabled={submitting}>
                  {submitting ? t('common.loading') : t('common.submit')} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
